import type { IForm } from '$/types/components/forms';
import type { TStatus } from './statusMap';
import type { UseMutationResult } from 'react-query';

import { yupResolver } from '@hookform/resolvers/yup';
import { useRouter } from 'next/router';
import { parse } from 'query-string';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import { toast } from 'react-toastify';
import * as yup from 'yup';

import ApiClient from '../../ApiClient';

import statusMap, { SignInStatus } from './statusMap';

import { toastContent } from '$/components/elements/Toasts';
import toastMessages from '$/components/elements/Toasts/toastMessages';
import useCurrentUser from '$/hooks/useCurrentUser';
import handleCommonToast from '$/utils/handleCommonToast';
import { exportToLocalStorage } from '$/utils/localstorage';

type SignInForm = {
  email: string;
};

export interface ISignInPageStateProps {
  onSubmit: (data: any) => Promise<void>;
  submitMutation: UseMutationResult;
  form: IForm<SignInForm>;
  statusText: TStatus;
  showContactPanel: boolean;
}

const disableServicePortalError = 'Service Portal is disabled for this user';
const emailNotFoundErrorMessage =
  'Diese E-Mail-Adresse gehört zu keinem Nutzerkonto.';
const disableServicePortalErrorComponent = (
  <div>
    Es tut uns leid, aber Dein Zugang zum Service-Portal wurde eingeschränkt
    oder gelöscht. Für weitere Informationen oder Unterstützung kontaktiere
    bitte unseren Kundenservice:{' '}
    <a href="mailto:kundenservice@deutsche-bildung.de">
      kundenservice@deutsche-bildung.de
    </a>
    .
  </div>
);

const useSignInPage = (): ISignInPageStateProps => {
  const { signIn } = useCurrentUser();
  const router = useRouter();
  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    setError,
    setValue,
  } = useForm<SignInForm>({
    mode: 'onBlur',
    resolver: yupResolver(
      yup.object({ email: yup.string().email().required() })
    ),
  });
  const [showContactPanel, setShowContactPanel] = useState(false);

  const statusText = useMemo(() => {
    const { status } = router.query as {
      status?: SignInStatus;
    };

    if (status && statusMap[status]) {
      return statusMap[status];
    }

    return statusMap[SignInStatus.DEFAULT];
  }, [router.query]);

  const submitMutation = useMutation(
    async (data: SignInForm) => {
      await router.replace(
        { pathname: router.pathname, query: {} },
        undefined,
        {
          shallow: true,
        }
      );

      const getDisableServicePortal = await ApiClient.disabledServicePortal({
        email: data.email,
      });
      try {
        if (
          getDisableServicePortal &&
          getDisableServicePortal.disableServicePortal
        ) {
          throw new Error(disableServicePortalError);
        }
        await signIn(data.email);
      } catch (error) {
        const userExists = await ApiClient.emailNotExistInCognito({
          email: data.email,
        });
        if (
          userExists?.exist &&
          getDisableServicePortal &&
          !getDisableServicePortal.disableServicePortal
        ) {
          // TODO: find a way to send a signup email with a first name provided
          await ApiClient.createUserData({
            email: data.email,
          });
          await signIn(data.email);
        } else {
          throw error;
        }
      }
    },
    {
      onError: (error: any) => {
        if (error?.message === disableServicePortalError) {
          setError('email', {
            type: 'disabled-sign-in',
            message: disableServicePortalErrorComponent,
          });
          return;
        }
        if (error?.code === 'UserNotFoundException') {
          setError('email', {
            type: 'email-not-found',
            message: emailNotFoundErrorMessage,
          });
          handleCommonToast(
            'error',
            error?.code || error,
            { autoClose: false },
            error
          );
          return;
        }
        error?.errors?.forEach(
          (
            errorMessage:
              | { message?: string; errorType?: string }
              | null
              | undefined
          ) => {
            const message =
              toastMessages['error'][String(errorMessage?.message)];
            if (!message) {
              if (
                errorMessage &&
                errorMessage.errorType &&
                errorMessage.message &&
                errorMessage.message !== 'User did not try to sign in'
              ) {
                toast(
                  toastContent('error', {
                    title: `Error: ${errorMessage.errorType}`,
                    description: errorMessage.message,
                  }),
                  {
                    type: 'error',
                    autoClose: false,
                  }
                );
              }
              return;
            }
          }
        );
        setShowContactPanel(true);
        return;
      },
    }
  );

  useEffect(() => {
    const { email } = router.query;

    // tODO: Add validation if this is a correct email address, use yup
    if (email) {
      setValue('email', email);
    }
  }, [setValue, router.query]);

  useEffect(() => {
    const query = parse(router.asPath.substr(router.asPath.indexOf('?') + 1));
    const { redirect } = query;

    if (redirect) {
      exportToLocalStorage('wordpress-redirect', {
        redirect,
        added: new Date().getTime(),
      });
    }
  }, [router.query]);

  return {
    onSubmit: submitMutation.mutateAsync,
    submitMutation,
    form: { control, formState: { errors, isSubmitting }, handleSubmit },
    statusText,
    showContactPanel,
  };
};

export default useSignInPage;
